import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { motion } from 'framer-motion'

const SCORING_LOGIC_CONTENT = `
# 📊 Scoring Engine

Scores are computed per-domain using a **staged, weighted evaluation** aligned with Gartner ITScore, then aggregated into an overall maturity level (1–5). Each domain has its own **pass rate threshold** (configurable in the Domains tab).

\`\`\`mermaid
flowchart LR
    A["📋 Answers"] --> B["🏷️ Group by Domain"]
    B --> C["📊 Evaluate Levels 1→5"]
    C --> D{"Weighted Pass Rate ≥ Domain Pass Rate?"}
    D -->|Yes| E["✅ Next Level"]
    D -->|No| F["🛑 Stop"]
    E --> C
    F --> G["🏆 Domain Maturity"]
    G --> H["📈 Average All Domains"]
    H --> I["🎯 Overall Maturity = floor(avg)"]
\`\`\`

---

## How It Works

1. **Group by Domain** — Scoring runs independently per domain (Governance, Protection, Availability, Control, Visibility)
2. **Sequential Levels (1→5)** — Questions are grouped by maturity level and evaluated in order. You must pass Level 1 before Level 2 is considered
3. **Weighted Pass Rate** — Each question has a weight (1.0–3.0). Pass rate = \`sum(passed weights) / sum(all weights)\`
4. **Per-Domain Threshold** — If weighted pass rate ≥ the domain's configured pass rate, the level is achieved. If not, **evaluation stops** — no higher level can be reached
5. **Empty Levels Auto-Pass** — Levels with zero questions are automatically passed
6. **Overall Maturity** — \`floor(average of all domain levels)\`. One weak domain pulls the whole score down

---

## Calculation Logic

**Per-Level Pass Rate:**
For each maturity level within a domain, the engine collects all active questions tagged at that level. Each question has a weight (default 1.0). The pass rate is calculated as:

\`\`\`
weighted_pass_rate = sum(weight of each PASSED question) / sum(weight of ALL questions at this level)
\`\`\`

If there are no questions at a level, the pass rate is treated as 100% (auto-pass). If a question is unanswered, it counts as not-passed but its weight still contributes to the total denominator, lowering the rate.

**Level Achievement:**
Starting from Level 1, the engine checks if \`weighted_pass_rate >= domain.pass_threshold\` (configured per domain in the Domains tab). If yes, the level is marked as achieved and the engine moves to the next level. If no, the engine **stops immediately** — the domain's maturity is the last level that was achieved. If even Level 1 fails, the domain defaults to Level 1.

**Domain Score (for charts):**
The domain score is normalized to a 0–1 scale: \`domain_score = achieved_level / 5.0\`. So Level 3 = 0.60, Level 5 = 1.00.

**Overall Organization Maturity:**
After scoring all domains independently, the overall maturity is \`floor(average of all domain maturity levels)\`. The floor function ensures the organization hasn't truly reached the next level until all domains support it.

**Threshold Auditing:**
The pass rate threshold used at score-calculation time is stored in the score's \`level_details\` JSON. This allows historical auditing — even if the threshold is changed later, you can see what threshold was active when any given score was calculated.

---

## Question Evaluation

| Type | Passed When |
|------|-------------|
| **Yes/No** | Answer = Yes (score = 1) |
| **MCQ** | Answer score ≥ question's maturity level |
| **Unanswered** | Always counts as **not passed** (penalizes score) |

**MCQ Example:** Question tagged Level 3 → Answer "Standardized" (3) → ✅ Passed. Answer "Informal" (2) → ❌ Not passed.

---

## Weight System

| Weight | Category | Example |
|--------|----------|---------|
| 1.0 | Standard | Baseline practices |
| 1.5–2.0 | Important | Core operational practices |
| 2.5–3.0 | Critical | Security, compliance, DR controls |

> A critical question (weight 3.0) failing has **3× the impact** of a standard question (weight 1.0).

---

## Worked Example: Governance Domain (Pass Rate: 70%)

\`\`\`mermaid
flowchart LR
    L1["Level 1 — 81.8% ✅"] --> L2["Level 2 — 63.6% ❌"]
    L2 -.->|Blocked| L3["Level 3 ⏸️"]
    L3 -.->|Blocked| L4["Level 4 ⏸️"]
    L4 -.->|Blocked| L5["Level 5 ⏸️"]
    style L1 fill:#d1fae5,stroke:#10b981
    style L2 fill:#fee2e2,stroke:#ef4444
    style L3 fill:#f3f4f6,stroke:#9ca3af
    style L4 fill:#f3f4f6,stroke:#9ca3af
    style L5 fill:#f3f4f6,stroke:#9ca3af
\`\`\`

**Level 1** — 4 questions (total weight 5.5), 3 passed (weight 4.5) → **81.8% ≥ 70% ✅**
**Level 2** — 3 questions (total weight 5.5), 2 passed (weight 3.5) → **63.6% < 70% ❌ → STOP**

**Result: Governance = Level 1.** Even with a strong L1, the chain breaks at L2.

---

## Overall Maturity Example

| Domain | Level | | Domain | Level |
|--------|-------|-|--------|-------|
| Governance | 1 | | Control | 4 |
| Protection | 3 | | Visibility | 3 |
| Availability | 3 | | | |

**Average = (1+3+3+4+3)/5 = 2.8 → floor(2.8) = Level 2 (Developing)**

---

## Configuration

| Parameter | Default | Notes |
|-----------|---------|-------|
| Domain Pass Rate | 70% | Configurable per domain in Assessment Engine → Domains tab |
| Default Weight | 1.0 | Applied when not explicitly set |
| Maturity Levels | 1–5 | Configurable via Maturity Levels tab |
`

export function ScoringLogicExplanation() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
        >
            <MarkdownPreview content={SCORING_LOGIC_CONTENT} />
        </motion.div>
    )
}

export default ScoringLogicExplanation
